import { useMemo } from "react";
import { getCurrentUserRole, hasRoleAccess } from "../lib";
import { UserRole } from "../constants";

/**
 * Hook for checking user permissions
 * @returns Object with permission checking functions
 */
export const usePermission = () => {
  const userRole = useMemo(() => getCurrentUserRole(), []);

  return {
    /** Check if user has any of the specified roles */
    hasRole: (roles: string[]) => {
      return hasRoleAccess({ access: roles });
    },

    /** Check if user is admin */
    isAdmin: () => hasRoleAccess({ access: [UserRole.ADMIN] }),

    /** Check if user is manager */
    isManager: () => hasRoleAccess({ access: [UserRole.MANAGER] }),

    /** Check if user is ops */
    isOps: () => hasRoleAccess({ access: [UserRole.OPS] }),

    /** Check if user is sales */
    isSales: () => hasRoleAccess({ access: [UserRole.SALES] }),

    /** Check if user is admin or manager */
    isAdminOrManager: () => hasRoleAccess({ access: [UserRole.ADMIN, UserRole.MANAGER] }),

    /** Check if user is admin, manager, or ops */
    isAdminManagerOrOps: () => hasRoleAccess({ access: [UserRole.ADMIN, UserRole.MANAGER, UserRole.OPS] }),

    /** Check if user is admin, manager, or sales */
    isAdminManagerOrSales: () => hasRoleAccess({ access: [UserRole.ADMIN, UserRole.MANAGER, UserRole.SALES] }),

    /** Check if user is authenticated */
    isAuthenticated: () => userRole !== null && userRole !== "",

    /** Get current user role */
    getCurrentRole: () => userRole,
  };
};
